import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  FileText,
  BarChart3,
  RefreshCw,
  Zap,
  Target,
  Activity,
  CheckCircle,
  Clock,
  XCircle,
  X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import Realtime from '../../services/realtime';
import { financialCaseApi } from '../../services/seller/financialCaseApi';
import { superAccountantApi } from '../../services/accountant/superAccountantApi';
import { 
  formatNumberWithCommas, 
  parseNumberFromFormatted, 
  numberToVietnameseText,
  numberToEnglishText
} from '../../utils/currencyUtils';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SellerFinance = ({ isSuperAccountant = false }) => {
  const { token, user } = useAuth();
  const [financialRecords, setFinancialRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [cancellingRecord, setCancellingRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({
    totalIncome: 0,
    totalExpense: 0,
    netProfit: 0,
    currencyStats: {
      VND: { totalIncome: 0, totalExpense: 0, netProfit: 0 },
      USD: { totalIncome: 0, totalExpense: 0, netProfit: 0 }
    }
  });
  const [chartData, setChartData] = useState(null);
  const [chartTitle, setChartTitle] = useState('Thống kê thu chi theo ngày');
  const [chartCurrency, setChartCurrency] = useState('VND'); // Thêm state cho chart currency

  // Helpers xử lý ngày tháng an toàn
  const formatDateYMD = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isValidDate = (dateObj) => !Number.isNaN(dateObj.getTime());

  // Load accountants for dropdown
  const loadAccountants = async () => {
    try {
      const api = isSuperAccountant ? superAccountantApi : financialCaseApi;
      const response = await api.getAccountants(token);
      if (response.success && response.data) {
        setAccountants(response.data);
      }
    } catch (error) {
      console.error('❌ [ERROR] Error loading accountants:', error);
      // Không hiển thị lỗi cho user, chỉ log
    }
  };

  // Load financial cases from API
  const loadFinancialCases = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const api = isSuperAccountant ? superAccountantApi : financialCaseApi;
      const response = await api.getFinancialCases(token);
      
      if (response.success && response.data) {
        let list = response.data;
        // Nếu là trang Tạo Financial Case của Super Accountant, chỉ hiển thị các case do họ tạo
        if (isSuperAccountant && user?.id) {
          list = list.filter(item => item?.created_by === user.id);
        }
        setFinancialRecords(list);
        setFilteredRecords(list);
      } else {
        setFinancialRecords([]);
        setFilteredRecords([]);
      }
    } catch (error) {
      console.error('❌ [ERROR] Error loading financial cases:', error);
      setError(`Lỗi khi tải dữ liệu: ${error.message}`);
      setFinancialRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      // Với Super Accountant ở trang tạo case, tính thống kê từ danh sách đã lọc (các case do họ tạo)
      if (isSuperAccountant) {
        setTimeout(() => {
          calculateStatisticsFromRecords();
        }, 0);
        return;
      }
      const api = isSuperAccountant ? superAccountantApi : financialCaseApi;
      const statsResponse = await api.getFinancialStatistics(token);
      
      if (statsResponse.success && statsResponse.data) {
        // Xử lý currency stats từ API
        const currencyStats = {
          VND: { totalIncome: 0, totalExpense: 0, netProfit: 0 },
          USD: { totalIncome: 0, totalExpense: 0, netProfit: 0 }
        };

        // Nếu API trả về currency_stats
        if (statsResponse.data.currency_stats) {
          statsResponse.data.currency_stats.forEach(stat => {
            if (stat.currency === 'VND' || stat.currency === 'USD') {
              currencyStats[stat.currency] = {
                totalIncome: stat.total_income || 0,
                totalExpense: Math.abs(stat.total_expense || 0),
                netProfit: stat.net_amount || 0
              };
            }
          });
        }

        const newStats = {
          totalIncome: statsResponse.data.overview?.total_income || 0,
          totalExpense: Math.abs(statsResponse.data.overview?.total_expense || 0),
          netProfit: statsResponse.data.overview?.net_amount || 0,
          currencyStats
        };
        setStatistics(newStats);
      } else {
        // Đảm bảo filteredRecords đã được cập nhật trước khi tính toán
        setTimeout(() => {
          calculateStatisticsFromRecords();
        }, 0);
      }
    } catch (error) {
      console.error('❌ [ERROR] Error loading statistics from API:', error);
      // Đảm bảo filteredRecords đã được cập nhật trước khi tính toán
      setTimeout(() => {
        calculateStatisticsFromRecords();
      }, 0);
    }
  };

  // Realtime: join seller room and listen to events
  useEffect(() => {
    if (!user || !user.email) return;
    Realtime.connect();
    Realtime.joinSellerRoom(user.email);

    const onCreated = (payload) => {
      if (!payload) return;
      toast.success(payload.message || 'Báo cáo tài chính đã được tạo!');
      loadFinancialCases();
      loadStatistics();
    };

    const onUpdated = (payload) => {
      if (!payload) return;
      const msg = payload.message || 'Báo cáo tài chính đã được cập nhật!';
      toast.success(msg);
      loadFinancialCases();
      loadStatistics();
    };

    Realtime.on('financial-case-created', onCreated);
    Realtime.on('financial-case-updated', onUpdated);

    return () => {
      Realtime.off('financial-case-created', onCreated);
      Realtime.off('financial-case-updated', onUpdated);
    };
  }, [user]);

  // Fallback calculation from records
  const calculateStatisticsFromRecords = () => {
    
    // Tính toán từ filteredRecords (đã được lọc theo thời gian và status)
    const totalIncome = filteredRecords
      .filter(record => record.type === 'income' && (record.status === 'completed' || record.status === 'approved'))
      .reduce((sum, record) => sum + record.amount, 0);
      
    const totalExpense = filteredRecords
      .filter(record => record.type === 'expense' && (record.status === 'completed' || record.status === 'approved'))
      .reduce((sum, record) => sum + record.amount, 0);
    
    const netProfit = totalIncome - totalExpense;
    

    
    // Tính toán theo currency - TÁCH BIỆT VND và USD
    const currencyStats = {
      VND: { totalIncome: 0, totalExpense: 0, netProfit: 0 },
      USD: { totalIncome: 0, totalExpense: 0, netProfit: 0 }
    };

    filteredRecords
      .filter(record => record.status === 'completed' || record.status === 'approved')
      .forEach(record => {
        const currency = record.currency || 'VND';
        if (currency === 'USD') {
          if (record.type === 'income') {
            currencyStats.USD.totalIncome += record.amount;
          } else {
            currencyStats.USD.totalExpense += record.amount;
          }
        } else {
          // VND
          if (record.type === 'income') {
            currencyStats.VND.totalIncome += record.amount;
          } else {
            currencyStats.VND.totalExpense += record.amount;
          }
        }
      });

    // Tính net profit cho từng currency
    currencyStats.VND.netProfit = currencyStats.VND.totalIncome - currencyStats.VND.totalExpense;
    currencyStats.USD.netProfit = currencyStats.USD.totalIncome - currencyStats.USD.totalExpense;

    setStatistics({
      totalIncome,
      totalExpense,
      netProfit,
      currencyStats: currencyStats || {
        VND: { totalIncome: 0, totalExpense: 0, netProfit: 0 },
        USD: { totalIncome: 0, totalExpense: 0, netProfit: 0 }
      }
    });

    // Generate chart data
    generateChartData();
  };

  // Generate chart data from financial records based on filter
  const generateChartData = () => {
    if (financialRecords.length === 0) return;

    let filteredRecords = financialRecords;
    let timeUnit = 'day'; // default
    let chartTitle = `Thống kê thu chi theo ngày (${chartCurrency})`;
    let startDate = new Date();
    let endDate = new Date();

    // Filter records based on dateFilter
    if (dateFilter !== 'all') {
      const today = new Date();
      
      switch (dateFilter) {
        case 'today':
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
          filteredRecords = financialRecords.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate && recordDate <= endDate;
          });
          timeUnit = 'hour';
          chartTitle = `Thống kê thu chi theo giờ hôm nay (${chartCurrency})`;
          break;
        case 'week':
          // Từ thứ 2 đến hôm nay
          const dayOfWeek = today.getDay(); // 0 = Chủ nhật, 1 = Thứ 2, ...
          const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Nếu là Chủ nhật thì lùi 6 ngày
          startDate = new Date(today);
          startDate.setDate(today.getDate() - daysFromMonday);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          
          filteredRecords = financialRecords.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate && recordDate <= endDate;
          });
          timeUnit = 'day';
          chartTitle = `Thống kê thu chi theo ngày (Tuần này - ${chartCurrency})`;
          break;
        case 'month':
          // Từ ngày 1 đến hôm nay
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          
          filteredRecords = financialRecords.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate && recordDate <= endDate;
          });
          timeUnit = 'day';
          chartTitle = `Thống kê thu chi theo ngày (Tháng này - ${chartCurrency})`;
          break;
        case 'quarter':
          // Từ đầu quý đến hôm nay
          const currentMonth = today.getMonth();
          const quarterStartMonth = Math.floor(currentMonth / 3) * 3; // 0, 3, 6, 9
          startDate = new Date(today.getFullYear(), quarterStartMonth, 1);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          
          filteredRecords = financialRecords.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate && recordDate <= endDate;
          });
          timeUnit = 'day';
          chartTitle = `Thống kê thu chi theo ngày (Quý này - ${chartCurrency})`;
          break;
        case 'year':
          // Từ đầu năm đến hôm nay
          startDate = new Date(today.getFullYear(), 0, 1);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          
          filteredRecords = financialRecords.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate && recordDate <= endDate;
          });
          timeUnit = 'day';
          chartTitle = `Thống kê thu chi theo ngày (Năm này - ${chartCurrency})`;
          break;
        default:
          timeUnit = 'day';
          chartTitle = `Thống kê thu chi theo ngày (${chartCurrency})`;
      }
    }



    // Group records by time unit (chỉ tính những record có status "Chấp thuận" và đúng currency)
    const recordsByTime = {};
    
    filteredRecords
      .filter(record => 
        (record.status === 'completed' || record.status === 'approved') &&
        record.currency === chartCurrency
      )
      .forEach(record => {
        const recordDate = new Date(record.date);
        if (!isValidDate(recordDate)) {
          // Bỏ qua bản ghi có ngày không hợp lệ để tránh crash
          return;
        }
        let timeKey;
        
        if (timeUnit === 'hour') {
          // Group by hour for today
          timeKey = `${recordDate.getFullYear()}-${String(recordDate.getMonth() + 1).padStart(2, '0')}-${String(recordDate.getDate()).padStart(2, '0')}-${String(recordDate.getHours()).padStart(2, '0')}`;
        } else {
          // Group by day for week/month/all
          timeKey = formatDateYMD(recordDate);
        }
        
        if (!recordsByTime[timeKey]) {
          recordsByTime[timeKey] = { income: 0, expense: 0 };
        }
        
        if (record.type === 'income') {
          recordsByTime[timeKey].income += record.amount;
        } else {
          recordsByTime[timeKey].expense += record.amount;
        }
      });

    // Generate time labels based on filter
    const timeLabels = [];
    const timeData = [];
    
    if (dateFilter === 'today') {
      // Generate 24 hours for today (0:00 to 23:00)
      const today = new Date();
      for (let hour = 0; hour < 24; hour++) {
        const timeKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}-${String(hour).padStart(2, '0')}`;
        timeLabels.push(`${hour}:00`);
        timeData.push({
          income: recordsByTime[timeKey]?.income || 0,
          expense: recordsByTime[timeKey]?.expense || 0
        });
      }
    } else if (dateFilter === 'week') {
      // Generate days from Monday to today
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const dateString = formatDateYMD(date);
        timeLabels.push(date.toLocaleDateString('vi-VN'));
        timeData.push({
          income: recordsByTime[dateString]?.income || 0,
          expense: recordsByTime[dateString]?.expense || 0
        });
      }
    } else if (dateFilter === 'month') {
      // Generate days from 1st to today
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const dateString = formatDateYMD(date);
        timeLabels.push(date.toLocaleDateString('vi-VN'));
        timeData.push({
          income: recordsByTime[dateString]?.income || 0,
          expense: recordsByTime[dateString]?.expense || 0
        });
      }
    } else if (dateFilter === 'quarter') {
      // Generate days from quarter start to today
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const dateString = formatDateYMD(date);
        timeLabels.push(date.toLocaleDateString('vi-VN'));
        timeData.push({
          income: recordsByTime[dateString]?.income || 0,
          expense: recordsByTime[dateString]?.expense || 0
        });
      }
    } else if (dateFilter === 'year') {
      // Generate days from year start to today
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const dateString = formatDateYMD(date);
        timeLabels.push(date.toLocaleDateString('vi-VN'));
        timeData.push({
          income: recordsByTime[dateString]?.income || 0,
          expense: recordsByTime[dateString]?.expense || 0
        });
      }
    } else {
      // For 'all' - use existing logic with all dates
      const existingDates = Object.keys(recordsByTime).sort();
      
      if (existingDates.length > 0) {
        const startDateObj = new Date(existingDates[0]);
        const endDateObj = new Date(existingDates[existingDates.length - 1]);
        
        if (isValidDate(startDateObj) && isValidDate(endDateObj)) {
          // Create all dates in range
          for (let date = new Date(startDateObj); date <= endDateObj; date.setDate(date.getDate() + 1)) {
            const dateString = formatDateYMD(date);
            timeLabels.push(new Date(dateString).toLocaleDateString('vi-VN'));
            timeData.push({
              income: recordsByTime[dateString]?.income || 0,
              expense: recordsByTime[dateString]?.expense || 0
            });
          }
        }
      }
    }



    const chartData = {
      labels: timeLabels,
      datasets: [
        {
          label: 'Thu nhập',
          data: timeData.map(item => item.income),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.1,
          fill: false
        },
        {
          label: 'Chi phí',
          data: timeData.map(item => item.expense),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.1,
          fill: false
        }
      ]
    };

    setChartData(chartData);
    setChartTitle(chartTitle);
  };

  // Load data when component mounts or token changes
  useEffect(() => {
    if (token) {
      loadFinancialCases();
      loadAccountants(); // Load danh sách accountants
    }
  }, [token]); // Only depend on token changes

  // Force load data on component mount
  useEffect(() => {
    // If we have a token but no data, load it
    if (token && financialRecords.length === 0) {
      loadFinancialCases();
    }
  }, []); // Run only once on mount

  // Ensure statistics are calculated when data is available
  useEffect(() => {
    // If we have data but statistics are empty, calculate them
    if (financialRecords.length > 0 && filteredRecords.length > 0 && 
        (statistics.totalIncome === 0 && statistics.totalExpense === 0 && statistics.netProfit === 0)) {
      setTimeout(() => {
        calculateStatisticsFromRecords();
      }, 100);
    }
  }, [financialRecords, filteredRecords, statistics]);

  // Auto-recalculate statistics when filteredRecords change
  useEffect(() => {
    if (filteredRecords.length >= 0) { // >= 0 để tính cả khi không có record
      calculateStatisticsFromRecords();
    }
  }, [filteredRecords]);

  useEffect(() => {
    filterRecords();
    // Regenerate chart when dateFilter changes
    if (financialRecords.length > 0) {
      generateChartData();
    }
  }, [searchTerm, statusFilter, dateFilter, customStartDate, customEndDate, currencyFilter, financialRecords]);

  // Effect để regenerate chart khi currency thay đổi
  useEffect(() => {
    if (financialRecords.length > 0) {
      generateChartData();
    }
  }, [chartCurrency, financialRecords]);

  const filterRecords = () => {
    console.log('🔍 [DEBUG] filterRecords called with:', {
      searchTerm,
      statusFilter,
      dateFilter,
      currencyFilter,
      totalRecords: financialRecords.length
    });
    
    let filtered = financialRecords;

    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }

    if (currencyFilter !== 'all') {
      filtered = filtered.filter(record => record.currency === currencyFilter);
    }

    if (dateFilter !== 'all') {
      const today = new Date();
      
      switch (dateFilter) {
        case 'today':
          const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= todayStart && recordDate <= todayEnd;
          });
          break;
        case 'week':
          // Từ thứ 2 đến hôm nay
          const dayOfWeek = today.getDay();
          const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - daysFromMonday);
          weekStart.setHours(0, 0, 0, 0);
          const weekEnd = new Date(today);
          weekEnd.setHours(23, 59, 59, 999);
          
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= weekStart && recordDate <= weekEnd;
          });
          break;
        case 'month':
          // Từ ngày 1 đến hôm nay
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          const monthEnd = new Date(today);
          monthEnd.setHours(23, 59, 59, 999);
          
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= monthStart && recordDate <= monthEnd;
          });
          break;
        case 'quarter':
          // Từ đầu quý đến hôm nay
          const currentMonth = today.getMonth();
          const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
          const quarterStart = new Date(today.getFullYear(), quarterStartMonth, 1);
          const quarterEnd = new Date(today);
          quarterEnd.setHours(23, 59, 59, 999);
          
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= quarterStart && recordDate <= quarterEnd;
          });
          break;
        case 'year':
          // Từ đầu năm đến hôm nay
          const yearStart = new Date(today.getFullYear(), 0, 1);
          const yearEnd = new Date(today);
          yearEnd.setHours(23, 59, 59, 999);
          
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= yearStart && recordDate <= yearEnd;
          });
          break;
        case 'custom':
          // Custom date range
          if (customStartDate && customEndDate) {
            const startDate = new Date(customStartDate);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(customEndDate);
            endDate.setHours(23, 59, 59, 999);
            
            filtered = filtered.filter(record => {
              const recordDate = new Date(record.date);
              return recordDate >= startDate && recordDate <= endDate;
            });
          }
          break;
      }
    }

    setFilteredRecords(filtered);
    
    // Tính thống kê ngay sau khi filter chấp thuận
    setTimeout(() => {
      calculateStatisticsFromRecords();
    }, 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    return type === 'income' ? 'text-green-600' : 'text-red-600';
  };

  const getTypeIcon = (type) => {
    return type === 'income' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />;
  };

  const handleCustomDateFilter = () => {
    if (customStartDate && customEndDate) {
      setDateFilter('custom');
      setShowCustomDateInputs(false);
    } else {
      toast.error('Vui lòng chọn cả ngày bắt đầu và ngày kết thúc');
    }
  };

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    setDateFilter(value);
    if (value !== 'custom') {
      setShowCustomDateInputs(false);
      setCustomStartDate('');
      setCustomEndDate('');
    } else {
      setShowCustomDateInputs(true);
    }
  };

  const formatCurrency = (amount, currency = 'VND') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }
    
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  // Form state for adding new financial case
  const [formData, setFormData] = useState({
    title: '',
    type: 'thu',
    amount: '',
    currency: 'VND',
    content: '',
    date: new Date().toISOString().split('T')[0], // Default to today's date
    assigned_accountant_id: '' // ID của accountant được chọn
  });
  const [amountInWords, setAmountInWords] = useState('');
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const [accountants, setAccountants] = useState([]); // Danh sách accountants

  // Tạo gợi ý linh động dựa trên số đã nhập
  const generateDynamicSuggestions = (inputValue) => {
    if (!inputValue || inputValue.length === 0) {
      setDynamicSuggestions([]);
      return;
    }
    
    const numericValue = parseInt(inputValue.replace(/,/g, ''));
    if (isNaN(numericValue) || numericValue === 0) {
      setDynamicSuggestions([]);
      return;
    }
    
    // Tạo các gợi ý với các bội số khác nhau
    const suggestions = [];
    
    // Gợi ý thông minh dựa trên độ dài số
    const numLength = numericValue.toString().length;
    
    if (numLength <= 2) {
      // Số 1-2 chữ số: gợi ý nhân với 1000, 10000, 100000, 1000000
      const multipliers = [1000, 10000, 100000, 1000000];
      multipliers.forEach(multiplier => {
        const suggestion = numericValue * multiplier;
        if (suggestion <= 1000000000) {
          suggestions.push(suggestion);
        }
      });
    } else if (numLength === 3) {
      // Số 3 chữ số: gợi ý nhân với 100, 1000, 10000
      const multipliers = [100, 1000, 10000];
      multipliers.forEach(multiplier => {
        const suggestion = numericValue * multiplier;
        if (suggestion <= 1000000000) {
          suggestions.push(suggestion);
        }
      });
    } else if (numLength === 4) {
      // Số 4 chữ số: gợi ý nhân với 10, 100, 1000
      const multipliers = [10, 100, 1000];
      multipliers.forEach(multiplier => {
        const suggestion = numericValue * multiplier;
        if (suggestion <= 1000000000) {
          suggestions.push(suggestion);
        }
      });
    } else {
      // Số lớn hơn: gợi ý nhân với 10, 100
      const multipliers = [10, 100];
      multipliers.forEach(multiplier => {
        const suggestion = numericValue * multiplier;
        if (suggestion <= 1000000000) {
          suggestions.push(suggestion);
        }
      });
    }
    
    // Thêm gợi ý làm tròn lên đến các mốc đẹp
    const roundUpSuggestions = [];
    const roundTargets = [1000, 5000, 10000, 50000, 100000, 500000, 1000000, 5000000, 10000000];
    
    roundTargets.forEach(target => {
      if (numericValue < target && target <= numericValue * 10) {
        roundUpSuggestions.push(target);
      }
    });
    
    // Kết hợp tất cả gợi ý
    const allSuggestions = [...suggestions, ...roundUpSuggestions];
    
    // Loại bỏ các giá trị trùng lặp và sắp xếp
    const uniqueSuggestions = [...new Set(allSuggestions)].sort((a, b) => a - b);
    
    // Giới hạn tối đa 6 gợi ý
    setDynamicSuggestions(uniqueSuggestions.slice(0, 6));
  };

  const handleAddRecord = () => {
    setShowAddModal(true);
    setFormData({
      title: '',
      type: 'thu',
      amount: '',
      currency: 'VND',
      content: '',
      date: new Date().toISOString().split('T')[0], // Default to today's date
      assigned_accountant_id: '' // Reset assigned accountant
    });
    setAmountInWords('');
    setDynamicSuggestions([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'amount') {
      const currentCurrency = formData.currency || 'VND';
      let cleanValue, formattedValue, numericValue;
      
      if (currentCurrency === 'USD') {
        // Cho USD: cho phép số, dấu chấm thập phân, và dấu phẩy ngăn cách hàng nghìn
        cleanValue = value.replace(/[^\d.,]/g, '');
        
        // Format số với dấu phẩy ngăn cách hàng nghìn và dấu chấm thập phân
        formattedValue = formatNumberWithCommas(cleanValue, 'USD');
        numericValue = parseNumberFromFormatted(formattedValue, 'USD');
      } else {
        // Cho VND: chỉ cho phép số và dấu phẩy
        cleanValue = value.replace(/[^\d,]/g, '');
        
        // Format số với dấu phẩy
        formattedValue = formatNumberWithCommas(cleanValue, 'VND');
        numericValue = parseNumberFromFormatted(formattedValue, 'VND');
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
      
      // Cập nhật chữ tiền theo loại tiền tệ
      if (numericValue > 0) {
        if (currentCurrency === 'USD') {
          setAmountInWords(numberToEnglishText(numericValue));
        } else {
          setAmountInWords(numberToVietnameseText(numericValue));
        }
      } else {
        setAmountInWords('');
      }
      
      // Tạo gợi ý linh động
      generateDynamicSuggestions(cleanValue);
    } else if (name === 'currency') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Cập nhật lại chữ tiền khi đổi loại tiền tệ
      if (formData.amount) {
        const numericValue = parseNumberFromFormatted(formData.amount);
        if (numericValue > 0) {
          if (value === 'USD') {
            setAmountInWords(numberToEnglishText(numericValue));
          } else {
            setAmountInWords(numberToVietnameseText(numericValue));
          }
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmitFinancialCase = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validation
      if (!formData.title || !formData.amount || !formData.content) {
        toast.error('Vui lòng điền đầy đủ thông tin!');
        return;
      }

      const numericAmount = parseNumberFromFormatted(formData.amount, formData.currency);
      if (numericAmount <= 0) {
        toast.error('Số tiền phải lớn hơn 0!');
        return;
      }


      
      const api = isSuperAccountant ? superAccountantApi : financialCaseApi;
      const response = await api.createFinancialCase(token, {
        title: formData.title,
        type: formData.type,
        amount: numericAmount,
        currency: formData.currency,
        content: formData.content,
        created_at: formData.date,
        assigned_accountant_id: formData.assigned_accountant_id || null
      });

      if (response.success) {
        toast.success('Tạo báo cáo tài chính thành công!');
        setShowAddModal(false);
        // Reload data
        await loadFinancialCases();
      } else {
        toast.error(response.message || 'Có lỗi xảy ra!');
      }
    } catch (error) {
      console.error('Error creating financial case:', error);
      toast.error(`Lỗi: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    setShowReportModal(true);
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
  };

  const handleDownloadReport = () => {
    try {
      // Tạo nội dung báo cáo
      const reportContent = `
BÁO CÁO TÀI CHÍNH
Ngày tạo: ${new Date().toLocaleDateString('vi-VN')}

TỔNG QUAN:
- Tổng thu: ${formatCurrency(statistics.totalIncome)}
- Tổng chi: ${formatCurrency(statistics.totalExpense)}
- Lợi nhuận: ${formatCurrency(statistics.netProfit)}

CHI TIẾT GIAO DỊCH:
${financialRecords.map(record => `
${record.title}
- Loại: ${record.type === 'income' ? 'Thu' : 'Chi'}
        - Số tiền: ${formatCurrency(record.amount, record.currency)} (${numberToVietnameseText(record.amount)})
        - Ngày: ${formatDate(record.date)}
        - Trạng thái: ${record.status === 'completed' || record.status === 'approved' ? 'Chấp thuận' : record.status === 'pending' ? 'Đang xử lý' : record.status === 'rejected' ? 'Từ chối' : record.status === 'cancelled' ? 'Đã hủy' : 'Từ chối'}
        - Mô tả: ${record.description}
        ${record.note ? `- Ghi chú: ${record.note}` : ''}
`).join('\n')}
      `.trim();

      // Tạo file và download
      const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bao-cao-tai-chinh-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Đã tải xuống báo cáo!');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Lỗi khi tải xuống báo cáo');
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord({
      id: record.id,
      title: record.title,
      type: record.type === 'income' ? 'thu' : 'chi',
      amount: formatNumberWithCommas(record.amount.toString()),
      currency: record.currency || 'VND',
      description: record.description,
      date: record.date,
      assigned_accountant_id: record.assigned_accountant_id || ''
    });
    setShowEditModal(true);
  };

  const handleCancelRecord = (record) => {
    setCancellingRecord(record);
    setShowCancelModal(true);
  };

  const confirmCancelRecord = async () => {
    if (!cancellingRecord) return;

    try {
      setLoading(true);
      const api = isSuperAccountant ? superAccountantApi : financialCaseApi;
      const response = await api.cancelFinancialCase(token, cancellingRecord.id);
      
      if (response.success) {
        toast.success('Đã hủy báo cáo thành công!');
        setShowCancelModal(false);
        setCancellingRecord(null);
        await loadFinancialCases(); // Reload data
      } else {
        toast.error(response.message || 'Lỗi khi hủy báo cáo');
      }
    } catch (error) {
      console.error('Error cancelling record:', error);
      toast.error('Lỗi khi hủy báo cáo');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!editingRecord) return;

    try {
      setLoading(true);
      
      const amount = parseNumberFromFormatted(editingRecord.amount, editingRecord.currency);
      if (amount <= 0) {
        toast.error('Số tiền phải lớn hơn 0');
        return;
      }

      const requestData = {
        title: editingRecord.title,
        type: editingRecord.type,
        amount: amount,
        currency: editingRecord.currency || 'VND',
        content: editingRecord.description,
        date: editingRecord.date,
        assigned_accountant_id: editingRecord.assigned_accountant_id || null
      };

      const api = isSuperAccountant ? superAccountantApi : financialCaseApi;
      const response = await api.updateFinancialCase(token, editingRecord.id, requestData);

      if (response.success) {
        toast.success('Cập nhật báo cáo thành công!');
        setShowEditModal(false);
        setEditingRecord(null);
        await loadFinancialCases(); // Reload data
      } else {
        toast.error(response.message || 'Lỗi khi cập nhật báo cáo');
      }
    } catch (error) {
      console.error('Error updating record:', error);
      toast.error('Lỗi khi cập nhật báo cáo');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalIncome = () => {
    // Chỉ tính VND cho tổng chính, USD hiển thị riêng
    return statistics.currencyStats?.VND?.totalIncome || 0;
  };

  const calculateTotalExpense = () => {
    // Chỉ tính VND cho tổng chính, USD hiển thị riêng
    return statistics.currencyStats?.VND?.totalExpense || 0;
  };

  const calculateNetProfit = () => {
    // Chỉ tính VND cho tổng chính, USD hiển thị riêng
    return statistics.currencyStats?.VND?.netProfit || 0;
  };

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 min-h-screen">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn { animation: fadeIn 0.6s ease-out; }
        .animate-slideInLeft { animation: slideInLeft 0.8s ease-out; }
        .animate-slideInRight { animation: slideInRight 0.8s ease-out; }
        .animate-fadeInUp { animation: fadeInUp 0.6s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.4s ease-out; }
      `}</style>
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white p-8 rounded-2xl shadow-2xl transform animate-scaleIn">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-200 border-t-green-600"></div>
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin" style={{animationDuration: '1.5s'}}></div>
              </div>
          <div>
                <p className="text-lg font-semibold text-gray-800">Đang tải dữ liệu...</p>
                <p className="text-sm text-gray-500">Vui lòng chờ trong giây lát</p>
          </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-lg shadow-lg animate-slideInRight">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="font-medium">{error}</span>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 transition-colors duration-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500 via-blue-600 to-purple-700 rounded-3xl shadow-2xl mb-8 p-8 text-white relative overflow-hidden animate-fadeIn">
        {/* Animated background elements */}
        <div className="absolute inset-0 bg-black opacity-5"></div>
        <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-3 rounded-full -translate-y-20 translate-x-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white opacity-3 rounded-full translate-y-16 -translate-x-16 animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white opacity-2 rounded-full animate-bounce" style={{animationDelay: '2s'}}></div>
        
        <div className="relative flex flex-col lg:flex-row justify-between items-start lg:items-center">
          <div className="animate-slideInLeft">
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-white bg-opacity-25 backdrop-blur-sm rounded-2xl shadow-lg">
                <DollarSign className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-green-100 bg-clip-text text-transparent">
                  {isSuperAccountant ? 'Tạo Financial Case' : 'Quản lý tài chính'}
                </h1>
                <p className="text-green-100 text-lg font-medium">
                  {isSuperAccountant 
                    ? 'Tạo và quản lý financial cases cho hệ thống' 
                    : 'Theo dõi thu chi và báo cáo tài chính chuyên nghiệp'
                  }
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 mt-8 lg:mt-0 animate-slideInRight">
            <button
              onClick={handleAddRecord}
              className="group flex items-center space-x-3 bg-white bg-opacity-25 backdrop-blur-sm text-white px-8 py-4 rounded-2xl hover:bg-white hover:text-emerald-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl border border-white border-opacity-20"
            >
              <Plus className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
              <span className="font-semibold text-lg">
                {isSuperAccountant ? 'Tạo Financial Case' : 'Thêm báo cáo'}
              </span>
            </button>
            <button
              onClick={handleGenerateReport}
              className="group flex items-center space-x-3 bg-white bg-opacity-25 backdrop-blur-sm text-white px-8 py-4 rounded-2xl hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl border border-white border-opacity-20"
            >
              <BarChart3 className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
              <span className="font-semibold text-lg">Báo cáo tài chính</span>
            </button>
            <button
              onClick={loadFinancialCases}
              className="group flex items-center space-x-3 bg-white bg-opacity-25 backdrop-blur-sm text-white px-8 py-4 rounded-2xl hover:bg-white hover:text-emerald-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl border border-white border-opacity-20"
              disabled={loading}
            >
              <RefreshCw className={`w-6 h-6 ${loading ? 'animate-spin' : 'group-hover:rotate-180'} transition-transform duration-300`} />
              <span className="font-semibold text-lg">Làm mới</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div className="group bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100 rounded-3xl shadow-xl p-8 border border-emerald-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 animate-fadeInUp" style={{animationDelay: '0.1s'}}>
          <div className="flex items-center justify-between mb-6">
            <div className="p-4 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-lg">
              <TrendingUp className="w-10 h-10 text-white" />
            </div>
            <div className="text-right">
              <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-600 mb-2 uppercase tracking-wide">Tổng thu nhập</p>
            <p className="text-4xl font-bold text-emerald-700 group-hover:scale-105 transition-transform duration-300 mb-3">
              {formatCurrency(calculateTotalIncome())}
            </p>
            <div className="flex items-center text-emerald-600 mb-4">
              <TrendingUp className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">Tăng trưởng tích cực</span>
            </div>
            {/* Multi-currency breakdown */}
            <div className="space-y-2">
              {statistics.currencyStats?.VND?.totalIncome > 0 && (
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <span className="text-sm font-medium text-emerald-700">VND</span>
                  <span className="text-sm font-bold text-emerald-800">
                    {formatCurrency(statistics.currencyStats.VND.totalIncome, 'VND')}
                  </span>
                </div>
              )}
              {statistics.currencyStats?.USD?.totalIncome > 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <span className="text-sm font-medium text-blue-700">USD</span>
                  <span className="text-sm font-bold text-blue-800">
                    {formatCurrency(statistics.currencyStats.USD.totalIncome, 'USD')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="group bg-gradient-to-br from-red-50 via-pink-50 to-red-100 rounded-3xl shadow-xl p-8 border border-red-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 animate-fadeInUp" style={{animationDelay: '0.2s'}}>
          <div className="flex items-center justify-between mb-6">
            <div className="p-4 bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl shadow-lg">
              <TrendingDown className="w-10 h-10 text-white" />
            </div>
            <div className="text-right">
              <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-red-600 mb-2 uppercase tracking-wide">Tổng chi phí</p>
            <p className="text-4xl font-bold text-red-700 group-hover:scale-105 transition-transform duration-300 mb-3">
              {formatCurrency(calculateTotalExpense())}
            </p>
            <div className="flex items-center text-red-600 mb-4">
              <TrendingDown className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">Chi phí vận hành</span>
            </div>
            {/* Multi-currency breakdown */}
            <div className="space-y-2">
              {statistics.currencyStats?.VND?.totalExpense > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl border border-red-200">
                  <span className="text-sm font-medium text-red-700">VND</span>
                  <span className="text-sm font-bold text-red-800">
                    {formatCurrency(statistics.currencyStats.VND.totalExpense, 'VND')}
                  </span>
                </div>
              )}
              {statistics.currencyStats?.USD?.totalExpense > 0 && (
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-xl border border-orange-200">
                  <span className="text-sm font-medium text-orange-700">USD</span>
                  <span className="text-sm font-bold text-orange-800">
                    {formatCurrency(statistics.currencyStats.USD.totalExpense, 'USD')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="group bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-3xl shadow-xl p-8 border border-blue-200 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 animate-fadeInUp" style={{animationDelay: '0.3s'}}>
          <div className="flex items-center justify-between mb-6">
            <div className={`p-4 rounded-2xl shadow-lg ${calculateNetProfit() >= 0 ? 'bg-gradient-to-br from-emerald-500 to-green-600' : 'bg-gradient-to-br from-red-500 to-pink-600'}`}>
              <DollarSign className="w-10 h-10 text-white" />
            </div>
            <div className="text-right">
              <div className={`w-3 h-3 rounded-full animate-pulse ${calculateNetProfit() >= 0 ? 'bg-emerald-400' : 'bg-red-400'}`} style={{animationDelay: '1s'}}></div>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-blue-600 mb-2 uppercase tracking-wide">Lợi nhuận ròng</p>
            <p className={`text-4xl font-bold group-hover:scale-105 transition-transform duration-300 mb-3 ${calculateNetProfit() >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatCurrency(calculateNetProfit())}
            </p>
            <div className="flex items-center text-blue-600 mb-4">
              <Target className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">Mục tiêu tài chính</span>
            </div>
            {/* Multi-currency breakdown */}
            <div className="space-y-2">
              {statistics.currencyStats?.VND?.netProfit !== 0 && (
                <div className={`flex items-center justify-between p-3 rounded-xl border ${statistics.currencyStats.VND.netProfit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <span className={`text-sm font-medium ${statistics.currencyStats.VND.netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>VND</span>
                  <span className={`text-sm font-bold ${statistics.currencyStats.VND.netProfit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                    {formatCurrency(statistics.currencyStats.VND.netProfit, 'VND')}
                  </span>
                </div>
              )}
              {statistics.currencyStats?.USD?.netProfit !== 0 && (
                <div className={`flex items-center justify-between p-3 rounded-xl border ${statistics.currencyStats.USD.netProfit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <span className={`text-sm font-medium ${statistics.currencyStats.USD.netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>USD</span>
                  <span className={`text-sm font-bold ${statistics.currencyStats.USD.netProfit >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                    {formatCurrency(statistics.currencyStats.USD.netProfit, 'USD')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

             {/* Chart Section */}
               {chartData && (
          <div className="bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl shadow-2xl mb-8 p-8 border border-gray-200 animate-fadeInUp" style={{animationDelay: '0.4s'}}>
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8">
              <div className="flex items-center space-x-4 mb-4 lg:mb-0">
                <div className="p-3 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl shadow-lg">
                  <Activity className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{chartTitle}</h3>
                  <p className="text-gray-600 text-sm">Biểu đồ thống kê thu chi theo thời gian</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-6">
                {/* Chart Currency Tabs */}
                <div className="flex bg-gray-100 rounded-2xl p-1 shadow-inner">
                  <button
                    onClick={() => setChartCurrency('VND')}
                    className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                      chartCurrency === 'VND'
                        ? 'bg-white text-emerald-600 shadow-lg border border-emerald-200'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                      <span>VND</span>
                    </span>
                  </button>
                  <button
                    onClick={() => setChartCurrency('USD')}
                    className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                      chartCurrency === 'USD'
                        ? 'bg-white text-blue-600 shadow-lg border border-blue-200'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center space-x-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      <span>USD</span>
                    </span>
                  </button>
                </div>
                {/* Chart Legend */}
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-emerald-500 rounded-full shadow-sm"></div>
                    <span className="font-medium text-emerald-700">Thu nhập</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-red-500 rounded-full shadow-sm"></div>
                    <span className="font-medium text-red-700">Chi phí</span>
                  </div>
                </div>
              </div>
            </div>
           <div className="h-96 bg-gradient-to-br from-white to-gray-50 rounded-3xl p-6 shadow-inner border border-gray-200">
                           <Line 
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    title: {
                      display: false
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      titleColor: 'white',
                      bodyColor: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      borderWidth: 2,
                      cornerRadius: 12,
                      padding: 12,
                      titleFont: {
                        size: 14,
                        weight: 'bold'
                      },
                      bodyFont: {
                        size: 13
                      },
                      callbacks: {
                        label: function(context) {
                          const label = context.dataset.label || '';
                          const value = context.parsed.y;
                          return `${label}: ${new Intl.NumberFormat(chartCurrency === 'USD' ? 'en-US' : 'vi-VN', {
                            style: 'currency',
                            currency: chartCurrency
                          }).format(value)}`;
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      grid: {
                        display: true,
                        color: 'rgba(0, 0, 0, 0.08)',
                        lineWidth: 1
                      },
                      ticks: {
                        maxRotation: 45,
                        minRotation: 0,
                        font: {
                          size: 12,
                          weight: '500'
                        },
                        color: '#6B7280'
                      }
                    },
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.08)',
                        lineWidth: 1
                      },
                      ticks: {
                        font: {
                          size: 12,
                          weight: '500'
                        },
                        color: '#6B7280',
                        callback: function(value) {
                          return new Intl.NumberFormat(chartCurrency === 'USD' ? 'en-US' : 'vi-VN', {
                            style: 'currency',
                            currency: chartCurrency,
                            minimumFractionDigits: chartCurrency === 'USD' ? 2 : 0,
                            maximumFractionDigits: chartCurrency === 'USD' ? 2 : 0
                          }).format(value);
                        }
                      }
                    }
                  },
                  elements: {
                    point: {
                      radius: 6,
                      hoverRadius: 10,
                      backgroundColor: 'white',
                      borderWidth: 3,
                      borderColor: function(context) {
                        return context.dataset.borderColor;
                      }
                    },
                    line: {
                      borderWidth: 4,
                      tension: 0.4
                    }
                  },
                  interaction: {
                    intersect: false,
                    mode: 'index'
                  }
                }}
              />
           </div>
         </div>
       )}

      {/* Filters */}
       <div className="bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl shadow-xl mb-8 p-8 border border-gray-200 animate-fadeInUp" style={{animationDelay: '0.5s'}}>
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Tìm kiếm báo cáo</label>
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6 group-focus-within:text-emerald-500 transition-colors duration-300" />
              <input
                type="text"
                placeholder="Nhập từ khóa tìm kiếm..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-14 pr-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 bg-white shadow-sm hover:shadow-md"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative group">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Trạng thái</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
                className="px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 bg-white appearance-none cursor-pointer pr-12 shadow-sm hover:shadow-md"
            >
                            <option value="all">Tất cả trạng thái</option>
              <option value="completed">Chấp thuận</option>
              <option value="pending">Đang xử lý</option>
              <option value="rejected">Từ chối</option>
              <option value="cancelled">Đã hủy</option>
            </select>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <div className="relative group">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Thời gian</label>
            <select
              value={dateFilter}
              onChange={handleDateFilterChange}
                className="px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 bg-white appearance-none cursor-pointer pr-12 shadow-sm hover:shadow-md"
            >
              <option value="all">Tất cả thời gian</option>
              <option value="today">Hôm nay</option>
              <option value="week">Tuần này</option>
              <option value="month">Tháng này</option>
                <option value="quarter">Quý này</option>
                <option value="year">Năm này</option>
                <option value="custom">Tùy chỉnh</option>
            </select>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            <div className="relative group">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Tiền tệ</label>
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
                className="px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 bg-white appearance-none cursor-pointer pr-12 shadow-sm hover:shadow-md"
            >
              <option value="all">Tất cả tiền tệ</option>
              <option value="VND">VND</option>
                              <option value="USD">USD</option>
            </select>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* Custom Date Range Inputs */}
        {showCustomDateInputs && (
          <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl border border-blue-200 animate-fadeIn shadow-lg">
            <div className="flex flex-col lg:flex-row gap-6 items-end">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-blue-700 mb-3">Từ ngày</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-6 py-4 border-2 border-blue-200 rounded-2xl focus:ring-4 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white shadow-sm hover:shadow-md"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-semibold text-blue-700 mb-3">Đến ngày</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-6 py-4 border-2 border-blue-200 rounded-2xl focus:ring-4 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 bg-white shadow-sm hover:shadow-md"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCustomDateFilter}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <Calendar className="w-5 h-5" />
                  Áp dụng
                </button>
                <button
                  onClick={() => {
                    setShowCustomDateInputs(false);
                    setDateFilter('all');
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }}
                  className="px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl hover:from-gray-600 hover:to-gray-700 transition-all duration-300 font-semibold flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <X className="w-5 h-5" />
                  Hủy
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Financial Records Table */}
      <div className="bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl shadow-2xl overflow-hidden animate-fadeInUp" style={{animationDelay: '0.6s'}}>
        {filteredRecords.length === 0 ? (
          <div className="p-16 text-center">
            <div className="text-gray-300 mb-8 animate-bounce">
              <svg className="mx-auto h-20 w-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Chưa có báo cáo tài chính</h3>
            <p className="text-gray-600 mb-8 text-lg">Bắt đầu bằng cách tạo báo cáo tài chính đầu tiên của bạn.</p>
            <button
              onClick={handleAddRecord}
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-semibold rounded-2xl shadow-xl text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 transform hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-6 h-6 mr-3" />
              Thêm báo cáo đầu tiên
            </button>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-emerald-500 via-blue-600 to-purple-600">
              <tr>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5" />
                    <span>Báo cáo</span>
                  </div>
                </th>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <Activity className="w-5 h-5" />
                    <span>Loại</span>
                  </div>
                </th>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-5 h-5" />
                    <span>Số tiền</span>
                  </div>
                </th>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5" />
                    <span>Ngày</span>
                  </div>
                </th>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Trạng thái</span>
                  </div>
                </th>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5" />
                    <span>Accountant</span>
                  </div>
                </th>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5" />
                    <span>Thời gian duyệt</span>
                  </div>
                </th>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5" />
                    <span>Ghi chú</span>
                  </div>
                </th>
                <th className="px-10 py-6 text-left text-sm font-bold text-white uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <Zap className="w-5 h-5" />
                    <span>Thao tác</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredRecords.map((record, index) => (
                <tr key={record.id} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all duration-300 transform hover:scale-[1.01] group" style={{animationDelay: `${0.7 + index * 0.1}s`}}>
                  <td className="px-10 py-8 whitespace-nowrap">
                    <div className="group-hover:transform group-hover:scale-105 transition-transform duration-300">
                      <div className="text-lg font-bold text-gray-900 mb-2">{record.title}</div>
                      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-200">{record.description}</div>
                    </div>
                  </td>
                  <td className="px-10 py-8 whitespace-nowrap">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-2xl shadow-lg ${record.type === 'income' ? 'bg-gradient-to-br from-emerald-100 to-green-100' : 'bg-gradient-to-br from-red-100 to-pink-100'}`}>
                      {getTypeIcon(record.type)}
                      </div>
                      <span className={`text-lg font-bold ${getTypeColor(record.type)}`}>
                        {record.type === 'income' ? 'Thu nhập' : 'Chi phí'}
                      </span>
                    </div>
                  </td>
                  <td className="px-10 py-8 whitespace-nowrap">
                    <div 
                      className={`text-xl font-bold ${getTypeColor(record.type)} cursor-help relative group`}
                      title={numberToVietnameseText(record.amount)}
                    >
                      <div className="p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200 hover:shadow-lg transition-all duration-300">
                        {formatCurrency(record.amount, record.currency)}
                      </div>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-4 py-3 bg-gray-900 text-white text-sm rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10 shadow-2xl">
                        {numberToVietnameseText(record.amount)}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-t-6 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-10 py-8 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-xl">
                        <Calendar className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-lg font-semibold text-gray-700">{formatDate(record.date)}</span>
                    </div>
                  </td>
                  <td className="px-10 py-8 whitespace-nowrap">
                    <span className={`inline-flex px-4 py-3 text-sm font-bold rounded-2xl shadow-lg ${getStatusColor(record.status)}`}>
                              {record.status === 'completed' || record.status === 'approved' ? 'Chấp thuận' :
         record.status === 'pending' ? 'Đang xử lý' :
         record.status === 'rejected' ? 'Từ chối' :
         record.status === 'cancelled' ? 'Đã hủy' : 'Từ chối'}
                    </span>
                  </td>
                  <td className="px-10 py-8 whitespace-nowrap">
                    {record.assigned_accountant_name ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-sm">
                            {record.assigned_accountant_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{record.assigned_accountant_name}</div>
                          <div className="text-xs text-gray-500">{record.assigned_accountant_email}</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">Chưa chỉ định</span>
                    )}
                  </td>
                  <td className="px-10 py-8 whitespace-nowrap">
                    {record.approved_at ? (
                      <div className="text-sm">
                        <div className="text-gray-900 font-medium">{formatDate(record.approved_at)}</div>
                        {record.approved_by_name && (
                          <div className="text-xs text-gray-500">bởi {record.approved_by_name}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">Chưa duyệt</span>
                    )}
                  </td>
                  <td className="px-10 py-8 whitespace-nowrap">
                    {record.note ? (
                      <div className="max-w-xs">
                        <div className="text-sm text-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-2xl border border-blue-200 shadow-sm">
                          {record.note}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded-xl border border-gray-200">Không có ghi chú</span>
                    )}
                  </td>
                  <td className="px-10 py-8 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleViewDetails(record)}
                        className="p-3 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-2xl transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl"
                        title="Xem chi tiết"
                      >
                        <Eye className="w-6 h-6" />
                      </button>
                      {record.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleEditRecord(record)}
                            className="p-3 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-2xl transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl"
                            title="Chỉnh sửa"
                          >
                            <Edit className="w-6 h-6" />
                          </button>
                          <button
                            onClick={() => handleCancelRecord(record)}
                            className="p-3 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-2xl transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-xl"
                            title="Hủy báo cáo"
                          >
                            <Trash2 className="w-6 h-6" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Add Record Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 animate-fadeIn">
          <div className="relative top-20 mx-auto p-8 border-0 w-full max-w-md shadow-2xl rounded-2xl bg-white animate-scaleIn">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Thêm báo cáo tài chính</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleSubmitFinancialCase} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Tiêu đề</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    placeholder="Nhập tiêu đề báo cáo"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Loại</label>
                  <select 
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    required
                  >
                    <option value="thu">Thu</option>
                    <option value="chi">Chi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Loại tiền tệ</label>
                                    <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    required
                  >
                    <option value="VND">VND (Việt Nam Đồng)</option>
                    <option value="USD">USD (Đô la Mỹ)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Accountant duyệt</label>
                  <select
                    name="assigned_accountant_id"
                    value={formData.assigned_accountant_id}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  >
                    <option value="">-- Chọn accountant (tùy chọn) --</option>
                    {accountants.map(accountant => (
                      <option key={accountant.id} value={accountant.id}>
                        {accountant.name} ({accountant.email})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Nếu không chọn, bất kỳ accountant nào cũng có thể duyệt
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ngày</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Số tiền</label>
                  <input
                    type="text"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    placeholder="Nhập số tiền (VD: 1,000,000)"
                    required
                  />
                  {amountInWords && (
                    <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700 font-medium">Bằng chữ:</p>
                      <p className="text-sm text-blue-800 italic font-semibold">{amountInWords}</p>
                    </div>
                  )}
                  {dynamicSuggestions.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs text-gray-500">Gợi ý:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {dynamicSuggestions.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => {
                              const currentCurrency = formData.currency || 'VND';
                              const formatted = formatNumberWithCommas(suggestion.toString(), currentCurrency);
                              setFormData(prev => ({ ...prev, amount: formatted }));
                              if (currentCurrency === 'USD') {
                                setAmountInWords(numberToEnglishText(suggestion));
                              } else {
                                setAmountInWords(numberToVietnameseText(suggestion));
                              }
                              setDynamicSuggestions([]); // Ẩn gợi ý sau khi chọn
                            }}
                            className="px-3 py-1 text-xs bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 border border-blue-200 rounded-lg transition-all duration-200 transform hover:scale-105"
                          >
                            {formatNumberWithCommas(suggestion.toString(), formData.currency || 'VND')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {dynamicSuggestions.length === 0 && formData.amount && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="text-xs text-gray-500">Gợi ý nhanh:</span>
                      {(formData.currency === 'USD' ? [0.25, 0.5, 1, 5, 10, 25, 50, 100] : [100000, 500000, 1000000, 5000000, 10000000]).map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => {
                            const currentCurrency = formData.currency || 'VND';
                            const formatted = formatNumberWithCommas(suggestion.toString(), currentCurrency);
                            setFormData(prev => ({ ...prev, amount: formatted }));
                            if (currentCurrency === 'USD') {
                              setAmountInWords(numberToEnglishText(suggestion));
                            } else {
                              setAmountInWords(numberToVietnameseText(suggestion));
                            }
                          }}
                          className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors duration-200"
                        >
                          {formatNumberWithCommas(suggestion.toString(), formData.currency || 'VND')}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mô tả</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    rows="4"
                    placeholder="Nhập mô tả chi tiết"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-3 text-base font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                    disabled={loading}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-green-500 to-green-600 rounded-xl hover:from-green-600 hover:to-green-700 disabled:opacity-50 transition-all duration-200 transform hover:scale-105"
                    disabled={loading}
                  >
                    {loading ? 'Đang tạo...' : 'Thêm'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Financial Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 animate-fadeIn">
          <div className="relative top-10 mx-auto p-8 border-0 w-11/12 max-w-5xl shadow-2xl rounded-2xl bg-white animate-scaleIn">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
                    <BarChart3 className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Báo cáo tài chính</h3>
                </div>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 shadow-lg transform hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-green-800">Tổng thu</h4>
                    <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
                  <p className="text-4xl font-bold text-green-700">{formatCurrency(calculateTotalIncome())}</p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-2xl border border-red-200 shadow-lg transform hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-red-800">Tổng chi</h4>
                    <TrendingDown className="w-8 h-8 text-red-600" />
                  </div>
                  <p className="text-4xl font-bold text-red-700">{formatCurrency(calculateTotalExpense())}</p>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl border border-blue-200 shadow-lg mb-8 transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-2xl font-bold text-blue-800">Lợi nhuận ròng</h4>
                  <DollarSign className="w-10 h-10 text-blue-600" />
                </div>
                <p className={`text-5xl font-bold ${calculateNetProfit() >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(calculateNetProfit())}
                </p>
              </div>
              
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="px-6 py-3 text-base font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                >
                  Đóng
                </button>
                <button 
                  onClick={handleDownloadReport}
                  className="px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl hover:from-blue-600 hover:to-blue-700 flex items-center space-x-2 transition-all duration-200 transform hover:scale-105"
                >
                  <Download className="w-5 h-5" />
                  <span>Tải xuống báo cáo</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Record Details Modal */}
      {selectedRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 animate-fadeIn">
            <div className="relative top-10 mx-auto p-6 border-0 w-full max-w-lg shadow-2xl rounded-2xl bg-white animate-scaleIn">
              <div className="mt-2">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
                      <FileText className="w-6 h-6 text-white" />
                </div>
                    <h3 className="text-xl font-bold text-gray-900">Chi tiết báo cáo</h3>
                  </div>
                  <button
                    onClick={() => setSelectedRecord(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Tiêu đề</label>
                    <p className="text-sm font-bold text-gray-900 truncate">{selectedRecord.title}</p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Ngày</label>
                    <div className="flex items-center space-x-2">
                      <div className="p-1 bg-blue-100 rounded">
                        <Calendar className="w-4 h-4 text-blue-600" />
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(selectedRecord.date)}</p>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Loại</label>
                    <div className="flex items-center space-x-2">
                      <div className={`p-1 rounded ${selectedRecord.type === 'income' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {selectedRecord.type === 'income' ? (
                          <TrendingUp className="w-4 h-4 text-green-600" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      <p className={`text-sm font-bold ${getTypeColor(selectedRecord.type)}`}>
                    {selectedRecord.type === 'income' ? 'Thu' : 'Chi'}
                  </p>
                </div>
                </div>
                  
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Trạng thái</label>
                    <div className="flex items-center space-x-2">
                                            <div className={`p-1 rounded ${
                        selectedRecord.status === 'completed' || selectedRecord.status === 'approved' ? 'bg-green-100' :
                        selectedRecord.status === 'pending' ? 'bg-yellow-100' : 'bg-red-100'
                      }`}>
                        {selectedRecord.status === 'completed' || selectedRecord.status === 'approved' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : selectedRecord.status === 'pending' ? (
                          <Clock className="w-4 h-4 text-yellow-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedRecord.status)}`}>
                                {selectedRecord.status === 'completed' || selectedRecord.status === 'approved' ? 'Chấp thuận' :
         selectedRecord.status === 'pending' ? 'Đang xử lý' :
         selectedRecord.status === 'rejected' ? 'Từ chối' :
         selectedRecord.status === 'cancelled' ? 'Đã hủy' : 'Từ chối'}
                      </span>
                </div>
              </div>
                </div>
                
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200 mb-4">
                  <label className="block text-sm font-semibold text-gray-600 mb-2">Số tiền</label>
                  <p className={`text-2xl font-bold ${getTypeColor(selectedRecord.type)} mb-2`}>
                    {formatCurrency(selectedRecord.amount)}
                  </p>
                  <p className="text-sm text-gray-600 italic">
                    {numberToVietnameseText(selectedRecord.amount)}
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200 mb-4">
                  <label className="block text-sm font-semibold text-gray-600 mb-2">Mô tả</label>
                  <p className="text-sm text-gray-700 leading-relaxed max-h-20 overflow-y-auto">{selectedRecord.description}</p>
                </div>
                
                {selectedRecord.note && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 mb-6">
                    <label className="block text-sm font-semibold text-blue-700 mb-2">Ghi chú (Lý do accept/reject)</label>
                    <p className="text-sm text-blue-800 leading-relaxed max-h-20 overflow-y-auto">{selectedRecord.note}</p>
                  </div>
                )}
                
                <div className="flex justify-end">
                <button
                  onClick={() => setSelectedRecord(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 transform hover:scale-105"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Record Modal */}
      {showEditModal && editingRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 animate-fadeIn">
          <div className="relative top-20 mx-auto p-8 border-0 w-full max-w-md shadow-2xl rounded-2xl bg-white animate-scaleIn">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Chỉnh sửa báo cáo</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingRecord(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleSubmitEdit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Tiêu đề</label>
                  <input
                    type="text"
                    value={editingRecord.title}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    placeholder="Nhập tiêu đề báo cáo"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Loại</label>
                  <select 
                    value={editingRecord.type}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    required
                  >
                    <option value="thu">Thu</option>
                    <option value="chi">Chi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Loại tiền tệ</label>
                                    <select
                    value={editingRecord.currency}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, currency: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    required
                  >
                    <option value="VND">VND (Việt Nam Đồng)</option>
                    <option value="USD">USD (Đô la Mỹ)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Accountant duyệt</label>
                  <select
                    value={editingRecord.assigned_accountant_id}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, assigned_accountant_id: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  >
                    <option value="">-- Chọn accountant (tùy chọn) --</option>
                    {accountants.map(accountant => (
                      <option key={accountant.id} value={accountant.id}>
                        {accountant.name} ({accountant.email})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Nếu không chọn, bất kỳ accountant nào cũng có thể duyệt
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ngày</label>
                  <input
                    type="date"
                    value={editingRecord.date}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Số tiền</label>
                  <input
                    type="text"
                    value={editingRecord.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      const formatted = formatNumberWithCommas(value);
                      setEditingRecord(prev => ({ ...prev, amount: formatted }));
                    }}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    placeholder="Nhập số tiền (VD: 1,000,000)"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mô tả</label>
                  <textarea
                    value={editingRecord.description}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    placeholder="Nhập mô tả báo cáo"
                    rows="3"
                    required
                  />
                </div>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingRecord(null);
                    }}
                    className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 transform hover:scale-105"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Đang cập nhật...' : 'Cập nhật'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && cancellingRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 animate-fadeIn">
          <div className="relative top-20 mx-auto p-8 border-0 w-full max-w-md shadow-2xl rounded-2xl bg-white animate-scaleIn">
            <div className="mt-3">
              <div className="flex items-center justify-center mb-6">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                  <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Xác nhận hủy báo cáo</h3>
                <p className="text-sm text-gray-600">
                  Bạn có chắc chắn muốn hủy báo cáo
                </p>
                <p className="text-lg font-semibold text-red-600 mt-2">
                  "{cancellingRecord.title}"?
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Hành động này không thể hoàn tác
                </p>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-xl border border-red-200 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-red-800">Thông tin báo cáo</h4>
                    <div className="mt-2 text-sm text-red-700">
                      <p><span className="font-medium">Loại:</span> {cancellingRecord.type === 'income' ? 'Thu' : 'Chi'}</p>
                      <p><span className="font-medium">Số tiền:</span> {formatCurrency(cancellingRecord.amount)}</p>
                      <p><span className="font-medium">Ngày:</span> {formatDate(cancellingRecord.date)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancellingRecord(null);
                  }}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 transform hover:scale-105"
                >
                  Không, giữ lại
                </button>
                <button
                  type="button"
                  onClick={confirmCancelRecord}
                  disabled={loading}
                  className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Đang hủy...</span>
                    </div>
                  ) : (
                    'Có, hủy báo cáo'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerFinance; 